import {InputNumber} from "primereact/inputnumber";
import {InputTextarea} from "primereact/inputtextarea";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import {formatValue} from "@reva/shared/number-format";
import type {ResolvedParameterSource} from "@reva/shared/parameter-resolution";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {navigateToRouteAndHash} from "../../hooks/navigationHooks";
import {ALL_SOURCES_TO_ROUTE} from "../../redux/objectParametersSlice";
import {resetOrEmptySectionII, selectCanResetSectionII} from "../../redux/sectionIIThunk";
import type {RevaDispatch} from "../../redux/store";
import {selectIndicationData} from "../../redux/valueIndicationSlice";
import {getAppraisalValueDetails} from "../../util/valueIndicationUtil";
import ParameterFieldContainer from "./ParameterFieldContainer";
import {getResourceColor} from "./paramHooks";

interface InputSectionIIEntriesDisabledProps {
    parameter: string;
    id: string;
    parameter_details_def?: object | null;
    associatedOutputParam?: string | null;
}

/** The fields of ParameterFieldContainer's render-control params this component reads. */
interface RenderControlParams {
    className?: string;
    activeParameterType?: ResolvedParameterSource | null;
    hasConflict?: boolean;
}

export function InputSectionIIEntriesDisabled({
    parameter,
    id,
    parameter_details_def,
    associatedOutputParam,
}: InputSectionIIEntriesDisabledProps) {
    const navigate = useNavigate();
    const {t, language} = useRevaTranslation();
    const indicationData = useSelector(selectIndicationData);
    const dispatch = useDispatch<RevaDispatch>();
    const canReset = useSelector(selectCanResetSectionII);

    const details = getAppraisalValueDetails(indicationData);
    const outputParamValue = (details?.section_ii_sum as number | undefined) ?? 0;
    const lendingSum = details?.section_ii_sum_lending as number | undefined;
    const onClick = () =>
        navigateToRouteAndHash(navigate, ALL_SOURCES_TO_ROUTE["register"], "register_section_ii_input");

    return (
        <ParameterFieldContainer
            id={id}
            label={t("object.section_ii_label")}
            parameter={parameter}
            parameterType={"REGISTER"}
            parameter_details_def={parameter_details_def}
            showUnit={true}
            showDescription={true}
            noReset={!canReset}
            onResetOrEmpty={(value: unknown) => dispatch(resetOrEmptySectionII(value))}
            hasAssociatedOutputParam={!!associatedOutputParam}
            renderControl={({className, activeParameterType, hasConflict}: RenderControlParams) => {
                const color = getResourceColor(activeParameterType, hasConflict);
                const filledPlaceholderClass = associatedOutputParam ? "indication-filled-placeholder" : "";

                if (lendingSum != null) {
                    return (
                        <div className="w-full cursor-pointer p-inputwrapper" onClick={onClick}>
                            <InputTextarea
                                id={id}
                                className={`w-full pointer-events-none text-right text-${color} ${className} ${parameter}`}
                                style={{resize: "none"}}
                                rows={2}
                                disabled={true}
                                value={`${t("object.tab_register.section_ii.market")}: ${formatValue(outputParamValue, language)}\n${t("object.tab_register.section_ii.lending")}: ${formatValue(lendingSum, language)}`}
                            />
                        </div>
                    );
                }

                return (
                    <InputNumber
                        inputId={id}
                        className={"w-full cursor-pointer"}
                        disabled={true}
                        inputClassName={`text-right text-${color} ${className} ${filledPlaceholderClass} ${parameter}`}
                        value={outputParamValue}
                        useGrouping={true}
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        locale={language}
                        onClick={onClick}
                    />
                );
            }}
        />
    );
}
